import React, { SetStateAction, useEffect, useState } from "react";

import {
	FrontendClient,
	FrontendCompanyTestCategory,
	FrontendCompanyWithAdditionalInfomation,
	FrontendOrder,
	FrontendOrganization,
	FrontendOrganizationLicense,
	FrontendSample,
	FrontendSampleInstance,
	FrontendSampleSpecification,
	FrontendSampleSpecificationDirectory,
	FrontendUserInfo,
} from "node-library/dist/portal";
import { useTranslation } from "react-i18next";

import { NeutralColor } from "node-library/dist/colors";
import { Severity, SystemRole } from "node-library/dist/constants";
import { deepClone } from "node-library/dist/helpers/misc_helper";
import { createUUID } from "node-library/dist/helpers/request_helper";
import Lottie from "react-lottie";
import { LongLoadingAnimation } from "../../common-components/animation/LongLoadingAnimation";
import { GreenButton, OutlinedButton } from "../../common-components/button";
import DiscardSampleConfirmModal from "../../common-components/modals/discardSampleConfirmModal";
import { ModalWithButtons } from "../../common-components/modals/modal";
import {
	RedErrorIcon,
	RedTrashIcon,
} from "../../common-components/toast/errorIcons";
import { useSnackbar } from "../../common-components/toast/showToastMessage";
import {
	GreenCopyIcon,
	GreenTickIcon,
} from "../../common-components/toast/successIcons";
import {
	YellowCrossIcon,
	YellowWarnIcon,
} from "../../common-components/toast/warnIcons";
import {
	downloadFile,
	uploadAllToS3,
} from "../../helpers/frontendFileOperationHelper";
import * as OrderSubmittedAnimationJSON from "../../images/animations/lotties/order_submitted.json";
import {
	sendGETRequest,
	sendPOSTRequest,
} from "../../requestHelper/sendRequest";
import NewOrderContent from "./newOrder/newOrderContent";
import OrderSubmissionConfirm from "./newOrder/orderSubmissionConfirm";
import NewSample from "./newSample/newSample";
import {
	hasPermission,
	PermissionMap,
	Permissions,
} from "node-library/dist/permissions";
import ImportOrderModal from "./newOrder/modal/importOrderModal";

interface Props {
	orderTestCategory: FrontendCompanyTestCategory;

	company: FrontendCompanyWithAdditionalInfomation;
	setCompany: React.Dispatch<
		React.SetStateAction<FrontendCompanyWithAdditionalInfomation | undefined>
	>;
	user: FrontendUserInfo | undefined;
	client: FrontendClient | undefined;

	order?: FrontendOrder;
	isRegisteringFromClientPortal?: boolean;

	orgInfoLoaded: boolean;
	selectedOrganization: FrontendOrganization;
	clientLicenses: Array<FrontendOrganizationLicense>;
	organizationSamples: Array<FrontendSample>;
	organizationSpecifications: Array<FrontendSampleSpecification>;
	organizationSpecDirectories: Array<FrontendSampleSpecificationDirectory>;

	gotoViewOrderPage: () => void;
	setUnsavedChanges: React.Dispatch<SetStateAction<boolean>>;

	goBackToPreviousPage?: () => void;
}

export default function NewOrEditOrder(props: Props) {
	const { t: translation } = useTranslation();
	const { openSnackbar } = useSnackbar();

	const user = props.user;
	const {
		company,
		setCompany,
		isRegisteringFromClientPortal,
		orderTestCategory,
	} = props;
	const setUnsavedChanges = props.setUnsavedChanges;

	const [currentPageIndex, setCurrentPageIndex] = useState<number>(1);

	//org info
	const [clientLicenses, setClientLicenses] = useState<
		Array<FrontendOrganizationLicense>
	>(props.clientLicenses);
	const [organizationSamples, setOrganizationSamples] = useState<
		Array<FrontendSample>
	>(props.organizationSamples);

	const [sampleSpecifications, setSampleSpecifications] = useState<
		Array<FrontendSampleSpecification>
	>(props.organizationSpecifications);

	const [specDirectories, setSpecDirectories] = useState<
		Array<FrontendSampleSpecificationDirectory>
	>(props.organizationSpecDirectories);
	//end org info

	const [licenseId, setLicenseId] = useState<string>(
		props.order ? props.order.licenseId || "" : "",
	);
	const [licenseError, setLicenseError] = useState<boolean>(false);
	const [loading, setLoading] = useState<boolean>(false);

	const [sampleInstances, setSampleInstances] = useState<
		Array<FrontendSampleInstance>
	>(props.order?.sampleInstances || []);
	const [updatingSampleInstance, setUpdatingSampleInstance] =
		useState<FrontendSampleInstance | null>(null);

	const [cocURL, setCocURL] = useState<string>("");
	const [submittedModalOpen, setSubmittedModalOpen] = useState<boolean>(false);

	const [importModalOpen, setImportModalOpen] = useState<boolean>(false);
	const [isUsingImport, setIsUsingImport] = useState<boolean>(false);

	//how do we include this when editing order?
	const [testReportEmails, setTestReportEmails] = useState<Array<string>>(
		props.order?.testReportCopyEmails || [],
	);
	const [billingInfoEmails, setBillingInfoEmails] = useState<Array<string>>(
		props.order?.billingInfoCopyEmails || [],
	);

	//billing information
	const [sameBillingInfoAsTestInfo, setSameBillingInfoAsTestInfo] =
		useState<boolean>(
			!(
				props.order?.billingName ||
				props.client?.defaultUseDifferentBillingInfo ||
				false
			),
		);
	const [billingName, setBillingName] = useState<string>(
		props.order?.billingName || props.client?.defaultBillingName || "",
	);
	const [billingAddress, setBillingAddress] = useState<string>(
		props.order?.billingAddress || props.client?.defaultBillingAddress || "",
	);
	const [city, setCity] = useState<string>(
		props.order?.billingCity || props.client?.defaultBillingCity || "",
	);
	const [postalCode, setPostalCode] = useState<string>(
		props.order?.billingPostalCode ||
			props.client?.defaultBillingPostalCode ||
			"",
	);
	const [province, setProvince] = useState<string>(
		props.order?.billingProvince || props.client?.defaultBillingProvince || "",
	);
	const [country, setCountry] = useState<string>(
		props.order?.billingCountry || props.client?.defaultBillingCity || "",
	);
	const [phoneNumber, setPhoneNumber] = useState<string>(
		props.order?.billingPhoneNumber ||
			props.client?.defaultBillingPhoneNumber ||
			"",
	);
	const [billingEmail, setBillingEmail] = useState<string>(
		props.order?.billingEmail || props.client?.defaultBillingEmail || "",
	);

	const [submittingOrder, setSubmittingOrder] = useState<boolean>(false);

	const [discardSampleConfirmOpen, setDiscardSampleConfirmOpen] =
		useState<boolean>(false);

	const [orderHasComment, setOrderHasComment] = useState<boolean>(
		props.order ? (props.order.comments ? true : false) : false,
	);
	const [orderComment, setOrderComment] = useState<string>(
		props.order ? props.order.comments || "" : "",
	);
	// const containsSameDayTests: boolean = sampleBatches.some((batch) => {
	// 	return batch.sampleTestInfo.some(
	// 		(sample) => sample.turnaroundTime === TurnaroundTime.SAME_DAY
	// 	);
	// });
	useEffect(() => {
		async function getOrganizationClientsAndLicenses() {
			setLoading(true);
			try {
				const response = await sendGETRequest(
					"/client-orgs/get-organization-detailed-info",
					{
						organizationId: props.selectedOrganization.organizationId,
						testCategoryId: orderTestCategory?.testCategoryId,
					},
				);
				const licenses: Array<FrontendOrganizationLicense> =
					response?.data?.licenses;
				setClientLicenses(licenses);

				const samples: Array<FrontendSample> = response?.data?.samples;
				setOrganizationSamples(samples);

				const specifications: Array<FrontendSampleSpecification> =
					response?.data?.specifications;
				setSampleSpecifications(specifications);

				const specDirectories: Array<FrontendSampleSpecificationDirectory> =
					response?.data?.specDirectories;
				setSpecDirectories(specDirectories);
			} catch (error: any) {
				console.error(error);
				openSnackbar(
					translation("SystemError"),
					Severity.ERROR,
					translation("SystemErrorMsg"),
					<RedErrorIcon />,
				);
			} finally {
				setLoading(false);
			}
		}
		if (!props.orgInfoLoaded) {
			getOrganizationClientsAndLicenses();
		}
	}, [props.selectedOrganization]);

	const needToReviewAllInstances: boolean =
		isRegisteringFromClientPortal || isUsingImport
			? sampleInstances.some((instance) => {
					return instance.pendingReview;
				})
			: false;

	function addSample(newSample: FrontendSampleInstance) {
		if (sampleInstances.length >= 70) {
			openSnackbar(
				translation("LimitReached"),
				Severity.WARN,
				translation("SampleLimitReachedDescription"),
				<YellowWarnIcon />,
			);
			return;
		}
		setSampleInstances((prevSamples) => [...prevSamples, newSample]);
		setUnsavedChanges(true);
	}

	function duplicateSample(sample: FrontendSampleInstance) {
		if (sampleInstances.length >= 70) {
			openSnackbar(
				translation("LimitReached"),
				Severity.WARN,
				translation("SampleLimitReachedDescription"),
				<YellowWarnIcon />,
			);
			return;
		}
		const sampleTestInfoClone = deepClone(sample.sampleTestInfo);

		const sampleTestBatch: FrontendSampleInstance = {
			...sample,
			sampleInstanceId: createUUID(),
			sampleTestInfo: sampleTestInfoClone,
		};
		addSample(sampleTestBatch);
		openSnackbar(
			translation("SampleDuplicated"),
			Severity.SUCCESS,
			undefined,
			<GreenCopyIcon />,
		);
	}
	function deleteSample(sampleInstanceId: string) {
		setSampleInstances((prevSamples) =>
			prevSamples.filter(
				(sample) => sample.sampleInstanceId !== sampleInstanceId,
			),
		);
		openSnackbar(
			translation("SampleDeleted"),
			Severity.ERROR,
			undefined,
			<RedTrashIcon />,
		);
		setUnsavedChanges(true);
	}
	function updateSample(sampleToUpdate: FrontendSampleInstance) {
		setSampleInstances((prevSamples) =>
			prevSamples.map((sample) =>
				sample.sampleInstanceId === sampleToUpdate.sampleInstanceId
					? sampleToUpdate
					: sample,
			),
		);
		setUnsavedChanges(true);
	}

	function discardSample() {
		if (updatingSampleInstance) {
			goBackToOrderPage();
		} else {
			setDiscardSampleConfirmOpen(true);
		}
	}
	function updateSampleInstancesFromImport(
		newSamples: Array<FrontendSample>,
		sampleInstances: Array<FrontendSampleInstance>,
	) {
		setOrganizationSamples((prevSamples) => [...prevSamples, ...newSamples]);

		setSampleInstances(sampleInstances);
		setIsUsingImport(true);
	}
	function goBackToOrderPage() {
		setUpdatingSampleInstance(null);
		setCurrentPageIndex(1);
	}

	function goToUpdateSample(sample: FrontendSampleInstance) {
		setUpdatingSampleInstance(sample);
		setCurrentPageIndex(2);
	}

	function goToAddNewSample() {
		if (sampleInstances.length >= 70) {
			openSnackbar(
				translation("LimitReached"),
				Severity.WARN,
				translation("SampleLimitReachedDescription"),
				<YellowWarnIcon />,
			);
			return;
		}
		setUpdatingSampleInstance(null);
		setCurrentPageIndex(2);
	}

	function goToSubmitOrder() {
		setCurrentPageIndex(4);
	}

	function gotoViewOrderPage() {
		setSubmittedModalOpen(false);
		props.gotoViewOrderPage();
	}
	async function discardOrder() {
		if (props.order) {
			//send a request to delete the temp order in the backend
			try {
				let response;
				response = await sendPOSTRequest("/orders/delete-order", {
					orderId: props.order.orderId,
				});

				//go to the submitted order page
				openSnackbar(
					translation("OrderDiscardSuccess"),
					Severity.WARN,
					undefined,
					<YellowCrossIcon />,
				);
				setUnsavedChanges(false);
				setTimeout(() => {
					props.gotoViewOrderPage();
				}, 500);
			} catch (error: any) {
				console.error(error);
				openSnackbar(
					translation("SystemError"),
					Severity.ERROR,
					translation("SystemErrorMsg"),
					<RedErrorIcon />,
				);
			}
		} else {
			//new order, just clear out the samples
			setSampleInstances([]);
			setUnsavedChanges(false);
			openSnackbar(
				translation("OrderDiscardSuccess"),
				Severity.WARN,
				undefined,
				<YellowCrossIcon />,
			);
			setTimeout(() => {
				props.gotoViewOrderPage();
			}, 500);
		}
	}
	async function submitOrder() {
		setSubmittingOrder(true);
		setSubmittedModalOpen(true);
		if (needToReviewAllInstances) {
			openSnackbar(
				translation("ActionRequired"),
				Severity.ERROR,
				translation("PleaseReviewAllInstancesErrorMsg"),
				<RedErrorIcon />,
			);
			return;
		}
		const doesUserHaveCreateOrderAccess: boolean = props.user
			? hasPermission(
					props.user?.role?.permissions as PermissionMap,
					Permissions.orders.create,
				)
			: false;
		const doesUserHaveEditOrderAccess: boolean = props.user
			? hasPermission(
					props.user?.role?.permissions as PermissionMap,
					Permissions.orders.updateStatus,
				)
			: false;
		const doesUserHaveUpdateOrderStatusAccess: boolean = props.user
			? hasPermission(
					props.user?.role?.permissions as PermissionMap,
					Permissions.orders.updateStatus,
				)
			: false;

		try {
			let response;
			if (
				!doesUserHaveCreateOrderAccess &&
				!doesUserHaveEditOrderAccess &&
				doesUserHaveUpdateOrderStatusAccess
			) {
				//sales rep can only update the instance test expectations and billing info
				response = await sendPOSTRequest("/orders/sales-rep-edit-order", {
					sampleInstances,
					licenseId,
					orderId: props.order ? props.order.orderId : undefined,
					useDifferentBillingInfo: !sameBillingInfoAsTestInfo,

					billingName,
					billingAddress,
					billingCity: city,
					billingProvince: province,
					billingCountry: country,
					billingPostalCode: postalCode,
					billingPhoneNumber: phoneNumber,
					billingEmail,
					testReportCopyEmails: testReportEmails,
					billingInfoCopyEmails: billingInfoEmails,
					orderComment: orderHasComment ? orderComment : undefined,
				});
			} else {
				//client portal specifications
				if (!props.order || isRegisteringFromClientPortal) {
					const newSpecificationsToUpload: Array<FrontendSampleSpecification> =
						[];
					const newSpecificationIds: Set<string> = new Set();
					for (const instance of sampleInstances) {
						if (
							instance.specification &&
							instance.specification.isNewSpecification &&
							!newSpecificationIds.has(instance.specification.specificationId)
						) {
							newSpecificationsToUpload.push(instance.specification);
							newSpecificationIds.add(instance.specification.specificationId);
						}
					}
					if (newSpecificationsToUpload.length > 0) {
						const specParams: Array<{
							fullName: string;
							fileSize: number;
							matrixId: string;
							type: string;
							clientPortalSpecId: string;
						}> = [];
						const specFiles: Array<File> = [];

						for (const newSpec of newSpecificationsToUpload) {
							const specFileBlob: Blob = await downloadFile(
								newSpec.downloadLink as string,
							);
							specParams.push({
								fullName: `${newSpec.fileName}.${newSpec.format}`,
								fileSize: specFileBlob.size,
								matrixId: newSpec.matrixId,
								type: specFileBlob.type,
								clientPortalSpecId: newSpec.clientPortalSpecId as string,
								//put it inside the default folder, no specDirectoryId
							});
							specFiles.push(
								new File(
									[specFileBlob],
									`${newSpec.fileName}.${newSpec.format}`,
									{ type: specFileBlob.type },
								),
							);
						}
						//upload the specifications first
						const response = await sendPOSTRequest(
							"/specifications/get-specification-presigned-upload-urls",
							{
								organizationId: props.selectedOrganization.organizationId,
								testCategoryId: orderTestCategory.testCategoryId,

								specFiles: specParams,
							},
						);

						const uploadBatchId: string = response.data.uploadBatchId;

						const uploadUrls: Array<{
							fileName: string;
							specificationId: string;
							presignedPost: AWS.S3.PresignedPost;
						}> = response.data.uploadUrls;
						const results = await uploadAllToS3(
							specFiles.map((file) => {
								return { file, progress: 0 };
							}),
							uploadUrls,
							() => {},
						);

						//check if all uploads are successful
						const errorSpecIds: Array<string> = [];

						if (results.some((result) => result == false)) {
							openSnackbar(
								translation("UnableToGetSpecifications"),
								Severity.ERROR,
								translation("UnableToGetSpecificationsDescription"),
								<RedErrorIcon />,
							);
							//clear all spec uploads
							const finalizeResponse = await sendPOSTRequest(
								"/specifications/finalize-specification-upload",
								{
									organizationId: props.selectedOrganization.organizationId,
									uploadBatchId,
									errorSpecIds: Array.from(newSpecificationIds),
								},
							);
							return;
						}
						const finalizeResponse = await sendPOSTRequest(
							"/specifications/finalize-specification-upload",
							{
								organizationId: props.selectedOrganization.organizationId,
								uploadBatchId,
								errorSpecIds: [],
							},
						);

						//update the specification ids with the new ones
						const insertedSpecs: Array<FrontendSampleSpecification> =
							finalizeResponse.data.insertedSpecs;

						for (const instance of sampleInstances) {
							if (
								instance.specification &&
								instance.specification.isNewSpecification
							) {
								const newSpecId: string = insertedSpecs.find((spec) => {
									return (
										spec.clientPortalSpecId ==
										instance.specification?.clientPortalSpecId
									);
								})?.specificationId as string;
								instance.specification.specificationId = newSpecId;
								instance.specification.isNewSpecification = false;
							}
						}
					}
					response = await sendPOSTRequest("/orders/submit-order", {
						orderTestCategoryId: orderTestCategory.testCategoryId,
						sampleInstances,
						licenseId,
						barcodeId: props.order?.orderBarcodeId || undefined,
						clientId: props.client?.clientId,

						useDifferentBillingInfo: !sameBillingInfoAsTestInfo,

						billingName,
						billingAddress,
						billingCity: city,
						billingProvince: province,
						billingCountry: country,
						billingPostalCode: postalCode,
						billingPhoneNumber: phoneNumber,
						billingEmail,
						testReportCopyEmails: testReportEmails,
						billingInfoCopyEmails: billingInfoEmails,
						orderComment: orderHasComment ? orderComment : undefined,
					});
				} else {
					response = await sendPOSTRequest("/orders/admin-edit-order", {
						sampleInstances,
						licenseId,
						orderId: props.order ? props.order.orderId : undefined,
						useDifferentBillingInfo: !sameBillingInfoAsTestInfo,

						billingName,
						billingAddress,
						billingCity: city,
						billingProvince: province,
						billingCountry: country,
						billingPostalCode: postalCode,
						billingPhoneNumber: phoneNumber,
						billingEmail,
						testReportCopyEmails: testReportEmails,
						billingInfoCopyEmails: billingInfoEmails,
						orderComment: orderHasComment ? orderComment : undefined,
					});
					// 	});
				}
			}

			setUnsavedChanges(false);
			//go to the submitted order page

			if (props.order && !isRegisteringFromClientPortal) {
				openSnackbar(
					translation("OrderDetailsUpdated"),
					Severity.SUCCESS,
					undefined,
					<GreenTickIcon />,
				);
				setSubmittedModalOpen(false);

				props.gotoViewOrderPage();
			}
		} catch (error: any) {
			console.log(translation("SubmitOrderError"));
			console.error(error);
			openSnackbar(
				translation("SystemError"),
				Severity.ERROR,
				translation("SystemErrorMsg"),
				<RedErrorIcon />,
			);
			setSubmittedModalOpen(false);

			goBackToOrderPage();
		} finally {
			setSubmittingOrder(false);
		}
	}

	let currentComponent: JSX.Element = <></>;
	switch (currentPageIndex) {
		// case 0:
		// 	currentComponent = (
		// 		<StepOneLicenseInfo
		// 			selectedClient={props.client}
		// 			licenseLoading={loading}
		// 			clientLicenses={clientLicenses}
		// 			setSelectedLicenseId={setLicenseId}
		// 			setCurrentPageIndex={setCurrentPageIndex}
		// 			goBack={props.goBackToClientDetailsPage}
		// 		/>
		// 	);
		// 	break;
		case 1:
			currentComponent = (
				<NewOrderContent
					company={company}
					setCompany={setCompany}
					user={user as FrontendUserInfo}
					orderTestCategory={orderTestCategory}
					licenseLoading={loading}
					isEditOrder={
						props.order && !isRegisteringFromClientPortal ? true : false
					}
					goBackToPreviousPage={props.goBackToPreviousPage}
					sampleInstances={sampleInstances}
					setSampleInstances={setSampleInstances}
					deleteSample={deleteSample}
					goToUpdateSample={goToUpdateSample}
					goToAddNewSample={goToAddNewSample}
					goToSubmitOrder={goToSubmitOrder}
					duplicateSample={duplicateSample}
					discardOrder={discardOrder}
					userLicenses={clientLicenses}
					licenseId={licenseId}
					setLicenseId={setLicenseId}
					licenseError={licenseError}
					setLicenseError={setLicenseError}
					needToReviewAllInstances={needToReviewAllInstances}
					isImportMode={isRegisteringFromClientPortal || isUsingImport}
					specDirectories={specDirectories}
					setSampleSpecifications={setSampleSpecifications}
					setImportModalOpen={setImportModalOpen}
					setUnsavedChanges={setUnsavedChanges}
				/>
			);
			break;
		case 2:
			currentComponent = (
				<NewSample
					company={company}
					setCompany={setCompany}
					user={user as FrontendUserInfo}
					orderTestCategory={orderTestCategory}
					organizationId={props.selectedOrganization.organizationId}
					addSample={addSample}
					updateSample={updateSample}
					discardSample={discardSample}
					sampleInstanceToUpdate={updatingSampleInstance}
					organizationSamples={organizationSamples}
					setOrganizationSamples={setOrganizationSamples}
					sampleSpecifications={sampleSpecifications}
					setSampleSpecifications={setSampleSpecifications}
					specDirectories={specDirectories}
					setSpecDirectories={setSpecDirectories}
					goBackToOrderPage={goBackToOrderPage}
					setUnsavedChanges={setUnsavedChanges}
				></NewSample>
			);
			break;
		case 4:
			currentComponent = (
				<OrderSubmissionConfirm
					company={company}
					setCompany={setCompany}
					user={user as FrontendUserInfo}
					isEditOrder={
						props.order && !isRegisteringFromClientPortal ? true : false
					}
					orderTestCategory={orderTestCategory}
					order={props.order}
					selectedClient={props.client}
					selectedOrganization={props.selectedOrganization}
					userLicenses={clientLicenses}
					licenseId={licenseId}
					sampleInstances={sampleInstances}
					testReportEmails={testReportEmails}
					billingInfoEmails={billingInfoEmails}
					setTestReportEmails={setTestReportEmails}
					setBillingInfoEmails={setBillingInfoEmails}
					goBackToOrderPage={goBackToOrderPage}
					sameBillingInfoAsTestInfo={sameBillingInfoAsTestInfo}
					setSameBillingInfoAsTestInfo={setSameBillingInfoAsTestInfo}
					billingName={billingName}
					setBillingName={setBillingName}
					billingAddress={billingAddress}
					setBillingAddress={setBillingAddress}
					city={city}
					setCity={setCity}
					postalCode={postalCode}
					setPostalCode={setPostalCode}
					province={province}
					setProvince={setProvince}
					country={country}
					setCountry={setCountry}
					phoneNumber={phoneNumber}
					setPhoneNumber={setPhoneNumber}
					billingEmail={billingEmail}
					setBillingEmail={setBillingEmail}
					orderHasComment={orderHasComment}
					setOrderHasComment={setOrderHasComment}
					orderComment={orderComment}
					setOrderComment={setOrderComment}
					submitOrder={() => {
						submitOrder();
					}}
					submittingOrder={submittingOrder}
					specDirectories={specDirectories}
					setSampleSpecifications={setSampleSpecifications}
				></OrderSubmissionConfirm>
			);
			break;
	}

	const orderSubmittedModal: React.ReactNode = (
		<ModalWithButtons
			headerText={""}
			open={submittedModalOpen}
			hideHeader={true}
			modalWidth={500}
			setOpen={(open) => {
				gotoViewOrderPage();
			}}
			buttons={
				submittingOrder
					? []
					: [
							<OutlinedButton
								key="modal-goto-view-orders-button"
								id="modal-goto-view-orders-button"
								children={translation("Vieworder")}
								onClick={gotoViewOrderPage}
								outlineColor={NeutralColor.NEUTRAL_GREY_950}
							></OutlinedButton>,
							<GreenButton
								key="modal-download-forms-button"
								id="modal-download-forms-button"
								children={translation("DownloadCoCform")}
								onClick={() => {
									window.open(cocURL, "_blank");
								}}
							></GreenButton>,
						]
			}
		>
			{submittingOrder ? (
				<div>
					<div style={{ display: "flex", justifyContent: "center" }}>
						<LongLoadingAnimation></LongLoadingAnimation>
					</div>
					<div
						className="center-text"
						style={{
							display: "flex",
							flexDirection: "column",
							alignItems: "center",
							gap: "5px",
						}}
					>
						<div
							className="typography-body-01 typography-bold"
							style={{ margin: "10px 0px" }}
						>
							{`${translation("SubmittingOrder")}`}
						</div>
						<div className="typography-caption">
							{translation("SubmittingOrderDescription")}
						</div>
						{/* {containsSameDayTests && (
						<div
							className="typography-caption"
							style={{
								backgroundColor: SemanticColor.WARNING_100,
								padding: "2px 6px",
								borderRadius: "4px",
							}}
						>
							{translation("SameDayContactText")}
						</div>
					)} */}
					</div>
				</div>
			) : (
				<div>
					<div className="confetti">
						<Lottie
							options={{
								loop: false,
								autoplay: true,
								animationData: OrderSubmittedAnimationJSON,
								rendererSettings: {
									preserveAspectRatio: "xMidYMid slice", // Aspect ratio settings
								},
							}}
						/>
					</div>
					<div
						className="center-text"
						style={{
							display: "flex",
							flexDirection: "column",
							alignItems: "center",
							gap: "5px",
						}}
					>
						<div
							className="typography-heading-03 typography-bold"
							style={{ margin: "10px 0px" }}
						>
							{`${translation("OrderSubmitted")}!`}
						</div>
						<div
							className="typography-caption"
							style={{ color: NeutralColor.NEUTRAL_GREY_500 }}
						>
							{translation("OrderSubmittedDescriptionText")}
						</div>
					</div>
				</div>
			)}
		</ModalWithButtons>
	);

	return (
		<div style={{ margin: "0 auto" }}>
			{currentComponent}
			{orderSubmittedModal}
			<DiscardSampleConfirmModal
				open={discardSampleConfirmOpen}
				setOpen={setDiscardSampleConfirmOpen}
				proceedAction={() => {
					goBackToOrderPage();
				}}
			></DiscardSampleConfirmModal>
			<ImportOrderModal
				open={importModalOpen}
				setOpen={setImportModalOpen}
				selectedTestCategory={orderTestCategory}
				organizationSamples={organizationSamples}
				organizationSpecifications={sampleSpecifications}
				updateSampleInstancesFromImport={updateSampleInstancesFromImport}
			></ImportOrderModal>
		</div>
	);
}
